/**
 * Text ↔ binary — pure, deterministic conversion via UTF-8 bytes.
 * Safe to render server-side.
 */

export type DecodeResult =
  | { ok: true; text: string }
  | { ok: false; error: string };

/** Encode text to space-separated 8-bit binary (UTF-8 bytes). */
export function textToBinary(text: string): string {
  const bytes = new TextEncoder().encode(text);
  return Array.from(bytes, (b) => b.toString(2).padStart(8, "0")).join(" ");
}

/** Decode binary (any whitespace grouping) back to text. */
export function binaryToText(bin: string): DecodeResult {
  const cleaned = bin.replace(/\s+/g, "");
  if (cleaned === "") return { ok: true, text: "" };
  if (!/^[01]+$/.test(cleaned)) {
    return { ok: false, error: "Only 0 and 1 (and spaces) are allowed." };
  }
  if (cleaned.length % 8 !== 0) {
    return { ok: false, error: "Binary length must be a multiple of 8." };
  }

  const bytes = new Uint8Array(cleaned.length / 8);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(cleaned.slice(i * 8, i * 8 + 8), 2);
  }

  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return { ok: true, text };
  } catch {
    return { ok: false, error: "Not valid UTF-8." };
  }
}
